import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useState, type ReactElement } from 'react';

import { Button, Empty, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { useTasks } from '../../tasks/hooks/useTasks.js';
import { TaskFormModal } from '../../tasks/components/TaskFormModal.js';
import { getTaskStatusLabel, STATUS_TONE as TASK_STATUS_TONE } from '../../tasks/lib/taskMeta.js';
import { formatDate } from '../../../shared/format/index.js';
import { i18n } from '../../../i18n/index.js';

interface ProjectTasksTabProps {
  projectId: string;
}

export function ProjectTasksTab({ projectId }: ProjectTasksTabProps): ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useTasks({ projectId });
  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium text-[var(--ant-color-text-secondary)]">
          {i18n.t('crm.features.features.projects.components.projecttaskstab.задачи_проекта', {
            p0: rows.length,
          })}
        </span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          data-testid="project-tasks-create"
        >
          {i18n.t('crm.features.features.projects.components.projecttaskstab.новая_задача')}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={i18n.t(
            'crm.features.features.projects.components.projecttaskstab.задач_нет',
          )}
        />
      ) : (
        <div className="flex flex-col divide-y divide-[var(--ant-color-border-secondary)] rounded-lg border border-[var(--ant-color-border)] bg-white">
          {rows.map((task) => (
            <div key={task.id} className="flex items-center gap-3 px-4 py-2.5">
              <Link
                to={`/tasks/${task.id}`}
                className="min-w-0 flex-1 truncate text-[13px] font-medium text-[var(--ant-color-text-heading)] hover:text-[var(--ant-color-primary)]"
              >
                {task.title}
              </Link>
              <EntityStatusChip tone={TASK_STATUS_TONE[task.status]} style={{ marginInlineEnd: 0 }}>
                {getTaskStatusLabel(task.status)}
              </EntityStatusChip>
              {task.dueAt ? (
                <span className="w-24 text-right text-xs text-[var(--ant-color-text-secondary)]">
                  {formatDate(task.dueAt)}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {createOpen ? (
        <TaskFormModal open lockedProjectId={projectId} onClose={() => setCreateOpen(false)} />
      ) : null}
    </div>
  );
}
